import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Plotly from 'plotly.js-basic-dist';
import createPlotlyComponent from 'react-plotly.js/factory';

const Plot = createPlotlyComponent(Plotly);

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const getDataYRange = (series, graphOverlays) => {
  let min = Infinity;
  let max = -Infinity;
  const consider = (raw) => {
    const v = Number(raw);
    if (Number.isFinite(v)) {
      if (v < min) min = v;
      if (v > max) max = v;
    }
  };

  series.forEach((s) => s.y.forEach(consider));

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    graphOverlays.forEach((o) => {
      consider(o.minValue);
      consider(o.maxValue);
    });
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];

  const pad = (max - min || Math.abs(max) || 1) * 0.1;
  return [min - pad, max + pad];
};

const SensorDataGraph = ({
  series, height, rangeDays, graphOverlays,
}) => {
  const data = useMemo(() => series.map((s) => {
    const { color, units, text } = s;

    const hasLabelText = Array.isArray(text)
      && text.some((t) => t !== '' && !Number.isFinite(Number(t)));

    let valueLine;
    if (hasLabelText) valueLine = '<b>%{text}</b>';
    else if (units) valueLine = `<b>%{y:.2f} ${units}</b>`;
    else valueLine = '<b>%{y:.2f}</b>';

    const title = s.sensorName ? `${s.sensorName} - ${s.name}` : s.name;

    return {
      type: 'scatter',
      mode: 'lines+markers',
      name: s.name,
      x: s.x,
      y: s.y,
      text,
      line: { color, width: 2, shape: 'linear' },
      marker: {
        color,
        size: 4,
        opacity: 0.6,
        line: { width: 0 },
      },
      hoveron: 'points',
      hoverlabel: {
        bgcolor: '#ffffff',
        bordercolor: color,
        font: { size: 12, color: '#212529' },
        align: 'left',
      },
      hovertemplate: `<b>${title}</b><br>`
        + '%{x|%b %d, %Y %I:%M %p}<br>'
        + `${valueLine}<extra></extra>`,
    };
  }), [series]);

  const yRange = useMemo(
    () => getDataYRange(series, graphOverlays),
    [series, graphOverlays],
  );

  const layout = useMemo(() => {
    const xRange = rangeDays
      ? [new Date(Date.now() - rangeDays * MS_PER_DAY), new Date()]
      : undefined;

    const shapes = graphOverlays.flatMap((o) => o.getOverlayShapes());

    const yAxisStyle = {
      fixedrange: false,
      autorange: true,
      autorangeoptions: { clipmin: yRange[0], clipmax: yRange[1] },
      showgrid: true,
      gridcolor: '#eef1f4',
      zeroline: false,
      showline: false,
      ticks: '',
      tickfont: { size: 11, color: '#6c757d' },
    };

    return {
      uirevision: 'sensor-data-graph',
      margin: {
        l: 48, r: 28, t: 12, b: 36,
      },
      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      plot_bgcolor: '#ffffff',
      paper_bgcolor: '#ffffff',
      shapes,
      xaxis: {
        type: 'date',
        fixedrange: false,
        autorange: !xRange,
        range: xRange,
        showgrid: true,
        gridcolor: '#eef1f4',
        zeroline: false,
        showline: false,
        ticks: '',
        tickfont: { size: 11, color: '#6c757d' },
        showspikes: true,
        spikemode: 'across',
        spikesnap: 'cursor',
        spikecolor: '#adb5bd',
        spikethickness: 1,
        spikedash: 'dot',
      },
      yaxis: yAxisStyle,
      yaxis2: {
        ...yAxisStyle,
        overlaying: 'y',
        side: 'right',
        showgrid: false,
      },
      hoverdistance: 20,
      spikedistance: -1,
      dragmode: 'zoom',
    };
  }, [rangeDays, graphOverlays, yRange]);

  const config = useMemo(() => ({
    displaylogo: false,
    responsive: true,
    scrollZoom: true,
    doubleClick: 'autosize',
    displayModeBar: 'hover',
    modeBarButtonsToRemove: ['lasso2d', 'select2d'],
  }), []);

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler
      style={{ width: '100%', height: height || '100%' }}
    />
  );
};

SensorDataGraph.propTypes = {
  series: PropTypes.arrayOf(PropTypes.shape({
    channelId: PropTypes.number,
    name: PropTypes.string.isRequired,
    color: PropTypes.string,
    units: PropTypes.string,
    x: PropTypes.arrayOf(PropTypes.instanceOf(Date)).isRequired,
    y: PropTypes.arrayOf(PropTypes.number).isRequired,
  })).isRequired,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rangeDays: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  graphOverlays: PropTypes.array,
};

SensorDataGraph.defaultProps = {
  height: '100%',
  rangeDays: null,
  graphOverlays: [],
};

export default SensorDataGraph;
